import { useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertCircle, Bot, CheckCircle2, History, Sparkles } from "lucide-react";
import AIFundingInput from "./ai-funding-modules/AIFundingInput";
import AIFundingHistoryModal from "./ai-funding-modules/AIFundingHistoryModal";
import AnalysisLog from "./ai-funding-modules/AnalysisLog";
import DealsPreview from "./ai-funding-modules/DealsPreview";
import InvestorsPreview from "./ai-funding-modules/InvestorsPreview";
import { runFundingAnalysisApi } from "./ai-funding-modules/aiFundingApi";

const MIN_CHARS = 100;
const MAX_CHARS = 1200;
const MIN_VISIBLE_ANALYSIS_MS = 2800;

function wait(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function waitForMinimumElapsed(startedAt, minimumMs) {
  const remainingMs = minimumMs - (Date.now() - startedAt);
  if (remainingMs > 0) {
    await wait(remainingMs);
  }
}

function formatUsd(amount) {
  if (amount == null || Number.isNaN(Number(amount))) return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(Number(amount));
}

function InsightCard({ title, value, hint }) {
  return (
    <div className="rounded-lg border border-stone-200 bg-stone-50/70 p-4">
      <div className="text-xs font-medium text-stone-500">{title}</div>
      <div className="mt-1 text-lg font-semibold tracking-tight text-stone-950">{value}</div>
      {hint ? <div className="mt-1 text-xs leading-5 text-stone-600">{hint}</div> : null}
    </div>
  );
}

export default function AIFunding() {
  const { t } = useTranslation();
  const [status, setStatus] = useState("idle");
  const [analysis, setAnalysis] = useState(null);
  const [errorInfo, setErrorInfo] = useState(null);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);

  const handleStart = async ({ description, minAmount, maxAmount }) => {
    const startedAt = Date.now();
    setStatus("running");
    setAnalysis(null);
    setErrorInfo(null);

    let result;
    try {
      result = await runFundingAnalysisApi({ description, minAmount, maxAmount });
    } catch (error) {
      await waitForMinimumElapsed(startedAt, MIN_VISIBLE_ANALYSIS_MS);
      setStatus("error");
      setErrorInfo({
        code: "NETWORK_ERROR",
        message: error?.message || t("app:AiFunding.requestFailed"),
      });
      return;
    }

    if (!result.ok) {
      await waitForMinimumElapsed(startedAt, MIN_VISIBLE_ANALYSIS_MS);
      setStatus("error");
      setErrorInfo({ code: result.code, message: result.message, analysis: result.analysis });
      return;
    }

    setAnalysis(result.analysis);
    await waitForMinimumElapsed(startedAt, MIN_VISIBLE_ANALYSIS_MS);
    setStatus("success");
  };

  const handleLoadHistoryAnalysis = (nextAnalysis) => {
    if (nextAnalysis?.status === "ok") {
      setAnalysis(nextAnalysis);
      setErrorInfo(null);
      setStatus("success");
      return;
    }

    setAnalysis(nextAnalysis || null);
    setErrorInfo({
      code: "NO_MATCH",
      message: nextAnalysis?.message || t("app:AiFunding.noMatchHistoryMessage"),
    });
    setStatus("error");
  };

  const narrative = analysis?.narrative;
  const market = analysis?.market;
  const matchedIndustries = Array.isArray(analysis?.matchedIndustries) ? analysis.matchedIndustries : [];

  return (
    <div className="min-h-full bg-stone-50/40 px-4 py-6 md:px-6">
      <div className="mx-auto grid max-w-7xl gap-5 xl:grid-cols-[minmax(0,1fr)_360px]">
        <main className="min-w-0 space-y-5">
          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => setIsHistoryOpen(true)}
              className="inline-flex h-8 cursor-pointer items-center gap-2 rounded-lg border border-stone-200 bg-white px-3 text-xs font-medium text-stone-700 shadow-sm hover:bg-stone-50"
            >
              <History className="h-3.5 w-3.5" />
              {t("app:AiFunding.historyButton")}
            </button>
          </div>

          <AIFundingInput
            minChars={MIN_CHARS}
            maxChars={MAX_CHARS}
            isLoading={status === "running"}
            onStart={handleStart}
          />

          {status === "idle" ? (
            <section className="rounded-lg border border-stone-200 bg-white p-5 shadow-sm">
              <div className="inline-flex items-center gap-2 rounded-full border border-stone-200 bg-stone-50 px-3 py-1 text-xs font-medium text-stone-600">
                <Bot className="h-3.5 w-3.5" />
                {t("app:AiFunding.emptyBadge")}
              </div>
              <h2 className="mt-3 text-xl font-semibold tracking-tight text-stone-950">
                {t("app:AiFunding.emptyTitle")}
              </h2>
              <p className="mt-2 max-w-3xl text-sm leading-6 text-stone-600">
                {t("app:AiFunding.emptyDescription")}
              </p>
            </section>
          ) : null}

          {status === "error" ? (
            <section className="rounded-lg border border-red-100 bg-white p-5 shadow-sm">
              <div className="flex items-start gap-3">
                <AlertCircle className="mt-0.5 h-5 w-5 text-red-500" />
                <div>
                  <h2 className="text-sm font-semibold text-stone-950">{t("app:AiFunding.errorTitle")}</h2>
                  <p className="mt-1 text-sm leading-6 text-stone-600">{errorInfo?.message}</p>
                </div>
              </div>
            </section>
          ) : null}

          {status === "success" && analysis ? (
            <>
              <section className="rounded-lg border border-stone-200 bg-white p-5 shadow-sm">
                <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                  <div>
                    <div className="inline-flex items-center gap-2 rounded-full border border-emerald-100 bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-700">
                      <CheckCircle2 className="h-3.5 w-3.5" />
                      {t("app:AiFunding.analysisReady")}
                    </div>
                    <h2 className="mt-3 text-xl font-semibold tracking-tight text-stone-950">
                      {narrative?.headline || t("app:AiFunding.defaultHeadline")}
                    </h2>
                    <p className="mt-2 max-w-3xl text-sm leading-6 text-stone-600">
                      {narrative?.brief}
                    </p>
                  </div>
                  <div className="inline-flex shrink-0 items-center gap-2 rounded-full border border-stone-200 bg-white px-3 py-1.5 text-xs font-medium text-stone-700 shadow-sm">
                    <Sparkles className="h-3.5 w-3.5" />
                    {analysis.primaryIndustry}
                  </div>
                </div>

                {matchedIndustries.length ? (
                  <div className="mt-4 flex flex-wrap gap-2">
                    {matchedIndustries.map((industry) => (
                      <span
                        key={industry}
                        className="inline-flex rounded-full bg-stone-50 px-3 py-1 text-xs font-medium text-stone-700 ring-1 ring-stone-200"
                      >
                        {industry}
                      </span>
                    ))}
                  </div>
                ) : null}

                <div className="mt-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                  <InsightCard
                    title={t("app:AiFunding.marketDeals")}
                    value={market?.totalDeals ?? 0}
                    hint={t("app:AiFunding.marketDealsHint")}
                  />
                  <InsightCard
                    title={t("app:AiFunding.medianAmount")}
                    value={formatUsd(market?.medianAmount)}
                    hint={t("app:AiFunding.knownAmounts", { count: market?.knownAmountDeals ?? 0 })}
                  />
                  <InsightCard
                    title={t("app:AiFunding.typicalRange")}
                    value={`${formatUsd(market?.p25Amount)} - ${formatUsd(market?.p75Amount)}`}
                    hint={t("app:AiFunding.rangeHint")}
                  />
                  <InsightCard
                    title={t("app:AiFunding.topRound")}
                    value={market?.topRounds?.[0]?.round || "—"}
                    hint={market?.topRounds?.[0] ? t("app:AiFunding.topRoundHint", { count: market.topRounds[0].count }) : ""}
                  />
                </div>

                {Array.isArray(narrative?.signals) && narrative.signals.length ? (
                  <div className="mt-5 rounded-lg border border-stone-200 bg-stone-50/70 p-4">
                    <h3 className="text-sm font-semibold text-stone-950">{t("app:AiFunding.signalsTitle")}</h3>
                    <ul className="mt-3 space-y-2">
                      {narrative.signals.map((signal) => (
                        <li key={signal} className="flex gap-2 text-sm leading-6 text-stone-600">
                          <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-stone-400" />
                          <span>{signal}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}
              </section>

              <DealsPreview deals={analysis.deals} market={market} />
              <InvestorsPreview investors={analysis.investors} notes={narrative?.investorNotes} />

              {Array.isArray(narrative?.nextSteps) && narrative.nextSteps.length ? (
                <section className="rounded-lg border border-stone-200 bg-white p-5 shadow-sm">
                  <h2 className="text-xl font-semibold tracking-tight text-stone-950">
                    {t("app:AiFunding.nextStepsTitle")}
                  </h2>
                  <div className="mt-4 grid gap-3 md:grid-cols-3">
                    {narrative.nextSteps.slice(0, 3).map((step, index) => (
                      <div key={step} className="rounded-lg border border-stone-200 bg-stone-50/70 p-4">
                        <div className="inline-flex h-7 w-7 items-center justify-center rounded-md bg-white text-xs font-semibold text-stone-900 shadow-sm ring-1 ring-stone-200">
                          {index + 1}
                        </div>
                        <p className="mt-3 text-sm leading-6 text-stone-600">{step}</p>
                      </div>
                    ))}
                  </div>
                </section>
              ) : null}
            </>
          ) : null}
        </main>

        <aside className="xl:sticky xl:top-6 xl:self-start">
          <AnalysisLog status={status} analysis={analysis} errorInfo={errorInfo} />
        </aside>
      </div>

      <AIFundingHistoryModal
        isOpen={isHistoryOpen}
        onClose={() => setIsHistoryOpen(false)}
        onLoadAnalysis={handleLoadHistoryAnalysis}
      />
    </div>
  );
}
